import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import toast from 'react-hot-toast';
import { useClickAway } from 'react-use';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { sendAiAssistantQuickAction } from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import { MAIN_QUICK_ACTIONS } from '../constants';
import { ALL, PLATFORM_LIST } from '../../../../../config/constants';
import {
  PLATFORM_COLORS,
  PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL,
  PLATFORM_ICONS
} from '../../../../../config/icons';
import {InfoIconWithToolTip} from "../../include/InfoIconWithToolTip";
import {advancedAiTokensAvailable} from "../utils/tokens";
import {UpgradeRequiredToolTip} from "./includes/UpgradeRequiredToolTip";
import LomavisUpgradeSubscriptionPageLink from "../../../../billing/utils/openSubscriptionPageLink";
import {useSelector} from "react-redux";




interface Props {
  t: (key: string) => string;
  dispatch: any;
  useSeparateTextFields: boolean;
  availableTokens: number;
  usedTokens: number; // if needed
  postData: any;
  advancedDataButton: boolean;
}

const RewriteTextAction: React.FC<Props> = ({
  t,
  dispatch,
  useSeparateTextFields,
  availableTokens,
  postData,
  usedTokens,
                                              advancedDataButton= false
}) => {
  const REWRITE_TEXT_ACTION = MAIN_QUICK_ACTIONS.find(
    (item) => item.action === 'rewriteText'
  );

  const [showRewriteDropdown, setShowRewriteDropdown] = useState(false);
  const dropdownRewriteRef = useRef<HTMLDivElement>(null);
  const [dropdownRewriteStyles, setDropdownRewriteStyles] =
    useState<React.CSSProperties>({});
  const dropdownRewriteTriggerRef = useRef<HTMLDivElement>(null);

  const {
    aiAssistant: {
      conversation: { messageSettings }
    }
  } = useSelector((state: any) => state.lomavisCreator);

  useEffect(() => {
    if (showRewriteDropdown && dropdownRewriteRef.current) {
      const triggerElement = dropdownRewriteTriggerRef.current;
      if (triggerElement) {
        const rect = triggerElement.getBoundingClientRect();
        setDropdownRewriteStyles({
          position: 'absolute',
          top: rect.bottom + window.scrollY - dropdownRewriteRef.current.offsetHeight,
          left: rect.left + window.scrollX,
          zIndex: 9999
        });
      }
    }
  }, [showRewriteDropdown]);

  useClickAway(dropdownRewriteRef, () => {
    setShowRewriteDropdown(false);
  });

  const handleRewriteTextClick = (platform?: string): void => {
    let textContent: string;

    if (!useSeparateTextFields) {
      textContent = postData?.[ALL]?.text_as_text || '';
    } else {
      textContent = postData?.[platform]?.text_as_text || '';
    }

    if (!textContent.trim()) {
      toast.error(t('lomavis.branding.mainActions.noText'), {
        duration: 2000,
        position: 'top-right'
      });
      return;
    }

    dispatch(
      sendAiAssistantQuickAction(REWRITE_TEXT_ACTION, {
        text: textContent,
        platform: platform ?? undefined,
        ...messageSettings
      }) as any
    );
  };

  const getPlatformsWithText = () => {
    return PLATFORM_LIST.filter((platform) => {
      return postData?.[platform]?.text_as_text?.trim() !== '';
    });
  };

  const RewriteDropdown = (
    <div
      style={dropdownRewriteStyles}
      ref={dropdownRewriteRef}
      className="dropdown-menu show p-4"
    >
      <h5 className="dropdown-header">
        {t('lomavis.branding.mainActions.selectPlatform')}
      </h5>
      <div className="dropdown-divider" />
      {getPlatformsWithText().map((platform) => (
        <div
          key={platform}
          className="d-flex align-items-center cursor-pointer dropdown-item px-1 py-2"
          onClick={() => {
            handleRewriteTextClick(platform);
            setShowRewriteDropdown(false);
          }}
        >
          <i
            className={`me-2 ${PLATFORM_ICONS[platform]} text-center w-25px`}
            style={{
              color: PLATFORM_COLORS[platform],
              fontSize: 20
            }}
          />
          {t(PLATFORM_DISPLAY_NAME_KEYS_WITH_ALL[platform])}
        </div>
      ))}
      {getPlatformsWithText().length === 0 && (
        <span className="dropdown-item disabled">
          {t('lomavis.branding.mainActions.noPlatforms')}
        </span>
      )}
    </div>
  );

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        !advancedAiTokensAvailable(usedTokens, availableTokens) ? (
            <Tooltip id="tooltip-no-tokens">
              <div className={"text-start"}>
                {t('lomavis.branding.mainActions.noTokensTooltip')}
              </div>
              <div className={"d-flex justify-content-end"}>
                <LomavisUpgradeSubscriptionPageLink />
              </div>
            </Tooltip>
        ) : (
          <></>
        )
      }
      trigger={
        !advancedAiTokensAvailable(usedTokens, availableTokens)
          ? ['hover', 'focus']
          : []
      }
    >
      <div
        key={REWRITE_TEXT_ACTION?.id}
        className="btn btn-secondary btn-sm cursor-pointer"
        style={{whiteSpace: "nowrap", backgroundImage: "linear-gradient(90deg, rgb(37, 117, 252), rgb(106, 17, 203))"}}

        onClick={() => {
          // if no available tokens, do nothing
          if (!advancedAiTokensAvailable(usedTokens, availableTokens)) {
            return;
          }

          if (useSeparateTextFields) {
            setShowRewriteDropdown((prev) => !prev);
          } else {
            handleRewriteTextClick();
          }
        }}
      >

        <div className="d-flex flex-row align-items-center" >
          <div className="d-flex align-items-center">
            <i className={`${REWRITE_TEXT_ACTION?.icon} me-2 w-20px text-white`} />
          </div>
          <div className="d-flex align-items-center text-white">
            {t(REWRITE_TEXT_ACTION?.name)}
          </div>
          <div className="d-flex align-items-center ps-2 text-white">
            <InfoIconWithToolTip text={t("lomavisCreator.ai.quickActionHelpText.rewriteText")} placement={"top"} showTokens={true}
                                 availableTokens={availableTokens}
                                 usedTokens={usedTokens}  advancedDataButton={true} />
          </div>
        </div>

        {/* Portal for the rewrite dropdown */}
        {useSeparateTextFields &&
          showRewriteDropdown &&
          ReactDOM.createPortal(RewriteDropdown, document.body)}
      </div>
    </OverlayTrigger>
  );
};

export default RewriteTextAction;
